import React, { useMemo, useState } from "react";
import { CustomButton } from "@/components/ui/Buttons";
import { Pencil } from "lucide-react";
import Modal from "@/components/ui/Modal";
import EditProjectDetailsModal from "./modals/EditProjectDetailsModal";
import { useAppSelector } from "@/redux/hooks/hooks";
import { TUser } from "@/types/types";

const isEmptyValue = (value: unknown) => {
  if (value === null || value === undefined) return true;

  if (typeof value === "string") return value.trim().length === 0;

  if (typeof value === "boolean") return false; // boolean is always valid data

  if (Array.isArray(value)) return value.length === 0;

  // objects (optional): treat empty object as empty
  if (typeof value === "object")
    return Object.keys(value as object).length === 0;

  return false;
};

const ProjectSetupDetails = ({ data }: { data: TUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const user = useAppSelector((state) => state.user?.user);
  const cleanedProps = data?.props?.map((p) => p.trim()).filter(Boolean) ?? [];
  const DetailField = ({
    label,
    value,
  }: {
    label: string;
    value: string | boolean | string[] | undefined;
  }) => (
    <div>
      <h5 className="text-sm font-medium text-gray-900 mb-1">{label}</h5>
      <p className="text-sm truncate w-[120px] lg:w-full text-gray-600">
        {Array.isArray(value)
          ? value.join(", ")
          : typeof value === "boolean"
            ? value
              ? "Yes"
              : "No"
            : value}
      </p>
    </div>
  );

  const fields = useMemo(() => {
    const list: Array<{
      key: string;
      label: string;
      value: string | boolean | string[] | undefined;
      // optional helper for grid placement
      grid?: boolean;
    }> = [
      {
        key: "hasRelativesInVideo",
        label: "Relatives/Peers in Video",
        value: data?.hasRelativesInVideo, // boolean: show if true or false (if present)
        grid: true,
      },
      {
        key: "props",
        label: "Available Props",
        value: cleanedProps, // string[]
        grid: true,
      },
      {
        key: "greenScreen",
        label: "Green Screen Setup",
        value: data?.greenScreen, // string
      },
      {
        key: "audioEquipment",
        label: "Audio Equipment",
        value: data?.noiselessRoomAndMicrophoneSpecs?.hasAudioEquipment
          ? data?.noiselessRoomAndMicrophoneSpecs?.details
          : undefined,
      },
      {
        key: "cameraInfo",
        label: "Camera Information",
        value: data?.cameraOrSmartphoneSpecs?.hasCamera
          ? data?.cameraOrSmartphoneSpecs?.details
          : undefined,
      },
      {
        key: "lightingInfo",
        label: "Lighting Setup",
        value: data?.lightingSpecs?.hasLighting
          ? data?.lightingSpecs?.details
          : undefined,
      },
      {
        key: "additionalExperience",
        label: "Additional Experience",
        value: data?.additionalInfo?.hasAdditionalExperience
          ? data?.additionalInfo?.details
          : undefined,
      },
    ];

    // keep only fields that actually have data
    return list.filter((f) => !isEmptyValue(f.value));
  }, [data]);

  // If nothing to show, hide the entire component
  if (fields.length === 0) return null;

  const gridFields = fields.filter((f) => f.grid);
  const restFields = fields.filter((f) => !f.grid);

  return (
    <>
      <div className="bg-white p-6 border border-[#E4E7EC] rounded-xl shadow space-y-5">
        <div className="flex justify-between items-center">
          <h6 className="text-[#344054] text-sm leading-5 font-medium">
            Project Setup Details
          </h6>

          <CustomButton
            onClick={() => setIsOpen(true)}
            variant="outline"
            color="#344054"
            fontSize="14px"
            padding="8px 12px"
            border="1px solid #E8EBEE"
            borderRadius="8px"
            className="flex items-center gap-1"
          >
            <Pencil className="size-4" />
            Edit
          </CustomButton>
        </div>

        {/* Only render this grid if we still have items for it */}
        {gridFields.length > 0 && (
          <div className="grid grid-cols-2 place-content-between gap-y-5">
            {gridFields.map((f) => (
              <DetailField key={f.key} label={f.label} value={f.value} />
            ))}
          </div>
        )}

        {restFields.map((f) => (
          <DetailField key={f.key} label={f.label} value={f.value} />
        ))}
      </div>

      <Modal
        preventCloseOnClickOutside={true}
        width="555px"
        isOpen={isOpen}
        setOpen={setIsOpen}
        padding="0px"
      >
        <EditProjectDetailsModal data={data} onClose={setIsOpen} />
      </Modal>
    </>
  );
};

export default ProjectSetupDetails;
// import React, { useState } from "react";
// import { CustomButton } from "@/components/ui/Buttons";
// import { Pencil } from "lucide-react";
// import Modal from "@/components/ui/Modal";
// import EditProjectDetailsModal from "./modals/EditProjectDetailsModal";
// import { useAppSelector } from "@/redux/hooks/hooks";
// import { TUser } from "@/types/types";

// // Types for project setup details
// interface ProjectSetupDetails {
//   hasRelativesInVideo?: boolean;
//   props?: string[];
//   greenScreen?: string;
//   audioEquipments?: string;
//   cameraInfo?: string;
//   lightingInfo?: string;
//   additionalExperience?: string;
// }

// const ProjectSetupDetails = ({ data }: { data: TUser }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const user = useAppSelector((state) => state.user?.user);

//   const DetailField = ({
//     label,
//     value,
//   }: {
//     label: string;
//     value: string | boolean | string[] | undefined;
//   }) => (
//     <div>
//       <h5 className="text-sm font-medium text-gray-900 mb-1">{label}</h5>
//       <p
//         className={`text-sm truncate w-[120px] lg:w-full ${
//           value === null || value === undefined
//             ? "text-red-500 font-medium"
//             : "text-gray-600"
//         }`}
//       >
//         {Array.isArray(value)
//           ? value.join(", ") || "Not provided"
//           : typeof value === "boolean"
//           ? value
//             ? "Yes"
//             : "No"
//           : value || "Not provided"}
//       </p>
//     </div>
//   );

//   return (
//     <>
//       <div className="bg-white p-6 border border-[#E4E7EC] rounded-xl shadow space-y-5">
//         <div className="flex justify-between items-center">
//           <h6 className="text-[#344054] text-sm leading-5 font-medium">
//             Project Setup Details
//           </h6>
//           <CustomButton
//             onClick={() => setIsOpen(true)}
//             variant="outline"
//             color="#344054"
//             fontSize="14px"
//             padding="8px 12px"
//             border="1px solid #E8EBEE"
//             borderRadius="8px"
//             className="flex items-center gap-1"
//           >
//             <Pencil className="size-4" />
//             Edit
//           </CustomButton>
//         </div>

//         <div className="grid grid-cols-2 place-content-between gap-y-5">
//           <DetailField
//             label="Relatives/Peers in Video"
//             value={data?.hasRelativesInVideo}
//           />
//           <DetailField label="Available Props" value={data?.props} />
//         </div>

//         <DetailField label="Green Screen Setup" value={data?.greenScreen} />
//         {/* need to change from here */}
//         <DetailField
//           label="Audio Equipment"
//           value={
//             data?.noiselessRoomAndMicrophoneSpecs.hasAudioEquipment
//               ? data?.noiselessRoomAndMicrophoneSpecs.details
//               : "-"
//           }
//         />
//         <DetailField
//           label="Camera Information"
//           value={
//             data?.cameraOrSmartphoneSpecs.hasCamera
//               ? data?.cameraOrSmartphoneSpecs.details
//               : "-"
//           }
//         />
//         <DetailField
//           label="Lighting Setup"
//           value={
//             data?.lightingSpecs.hasLighting ? data?.lightingSpecs.details : "-"
//           }
//         />
//         <DetailField
//           label="Additional Experience"
//           value={
//             data?.additionalInfo.hasAdditionalExperience
//               ? data?.additionalInfo.details
//               : "-"
//           }
//         />
//       </div>

//       <Modal
//         preventCloseOnClickOutside={true}
//         width="555px"
//         isOpen={isOpen}
//         setOpen={setIsOpen}
//         padding="0px"
//       >
//         <EditProjectDetailsModal data={data} onClose={setIsOpen} />
//       </Modal>
//     </>
//   );
// };

// export default ProjectSetupDetails;
